import styles from "./main-layout.module.css";

import { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  Layout,
  Button,
  Spin,
  Flex,
  Dropdown,
  Popover,
  Tooltip,
  Skeleton,
} from "antd";
import { setTwoToneColor } from "@ant-design/icons";

import Logo from "@/components/ui/Logo";
import Menu from "@/components/ui/Menu";
import NewsFeed from "@/components/ui/NewsFeed";
import SearchResultItem from "./components/SearchResultItem";
import MessengerFloatingWindow from "@/components/messenger/MessengerFloatingWindow";
import {
  handleMessengerIncomingNotification as dispatchMessengerIncomingNotification,
  initMessengerNotificationSoundUnlock,
  resolveMessengerNotificationSoundUrl,
  resolveMessengerRingSoundUrl,
} from "@/utils/messengerNotifications";
import { messengerOriginsMatch } from "@/utils/messengerOrigin";

import SettingsIcon from "@/icons/SettingsIcon";
import UnreadPanel from "@/features/unread/UnreadPanel";
import LogoutIcon from "@/icons/LogoutIcon";
import UserCircleIcon from "@/icons/UserCircleIcon";
import NoticesCountBadge from "@/features/unread/NoticesCountBadge";
import UserIcon from "@/icons/UserIcon";
import DoubleRightIcon from "@/icons/DoubleRightIcon";
import MessengerCountBadge from "@/features/unread/MessengerCountBadge";
import DoubleLeftIcon from "@/icons/DoubleLeftIcon";
import ChatAltIcon from "@/icons/ChatAltIcon";
import BellIcon from "@/icons/BellIcon";
import useUnreadCommands from "@/features/unread/useUnreadCommands";
import MenuIcon from "@/icons/MenuIcon";
import BrowserNotifyPrompt from "@/features/browserNotify/BrowserNotifyPrompt";
import CloseIcon from "@/icons/CloseIcon";
import AdminIcon from "@/icons/AdminIcon";
import isDesktopNotifyEnabled from "@/features/browserNotify/isDesktopNotifyEnabled";
import LoginIcon from "@/icons/LoginIcon";
import SearchIcon from "@/icons/Search";

import { getMenu } from "@/api/definitionsMenu";
import { getEmployeeUnitsOptions } from "@/api/units";
import { logout } from "@/api/auth";
import { getVersion } from "@/api/core";
import { getList as getAnalyticReports } from "@/api/analyticReports";
import { getList as getReports } from "@/api/reports";
import { getFormList as getRequests } from "@/api/requests";
import { getList as getNotices } from "@/api/notices";
import { globalSearch } from "@/api/search";

import {
  clearCachedMessengerBootstrap,
  createMessengerBootstrap,
  startMessengerBootstrapAutoRefresh,
} from "@/api/messenger";
import routes from "@/routes";
import { getPluralizedWord } from "@/utils";

import MainContext from "./context";
import { UserContext } from "@/context/UserContext";
import InputFrom3 from "@/components/inputs/InputFrom3";

const { Content, Sider, Header } = Layout;

const withOptionalTooltip = (enabled, tooltipProps, children) => {
  if (!enabled) {
    return children;
  }

  return <Tooltip {...tooltipProps}>{children}</Tooltip>;
};

const MESSENGER_BOOTSTRAP_TYPE = "teamtools-messenger-bootstrap";
const MESSENGER_READY_TYPE = "teamtools-messenger-ready";
const MESSENGER_AUTHENTICATED_TYPE = "teamtools-messenger-authenticated";
const MESSENGER_RECOVER_SESSION_TYPE = "teamtools-messenger-recover-session";
const MESSENGER_UNREAD_TYPE = "teamtools-messenger-unread";
const MESSENGER_NOTIFICATION_TYPE = "teamtools-messenger-notification";
const MESSENGER_PLAY_SOUND_TYPE = MESSENGER_NOTIFICATION_TYPE;
const MESSENGER_TAB_CLOSED_TYPE = "teamtools-messenger-tab-closed";
const MESSENGER_DELEGATED_STATE_TYPE = "teamtools-messenger-delegated-state";
const MESSENGER_FOCUS_EXTERNAL_TAB_TYPE = "teamtools-messenger-focus-external-tab";
const MESSENGER_FOCUS_ACK_TYPE = "teamtools-messenger-focus-ack";
const MESSENGER_TAKEOVER_REQUEST_TYPE = "teamtools-messenger-takeover-request";
const MESSENGER_TAKEOVER_ACK_TYPE = "teamtools-messenger-takeover-ack";
const MESSENGER_FOCUS_ACK_TIMEOUT_MS = 900;
const MESSENGER_TAKEOVER_TIMEOUT_MS = 3000;
const MESSENGER_HANDOFF_TIMEOUT_MS = 6000;
const MESSENGER_REOPEN_EXTERNAL_TAB_TIMEOUT_MS = 20000;
const MESSENGER_TAB_CLOSE_WAIT_MS = 10000;
const MESSENGER_TAB_WINDOW_NAME = "teamtools-messenger";
const MESSENGER_TAB_SYNC_INTERVAL_MS = 1000;
const MESSENGER_DELEGATE_CLEAR_DELAY_MS = 4000;
const MESSENGER_DELEGATED_STORAGE_KEY = "teamtools-messenger-delegated";
const MESSENGER_DELEGATED_TAB_ID_STORAGE_KEY = "teamtools-messenger-delegated-tab-id";
const MESSENGER_DELEGATED_OWNER_STORAGE_KEY = "teamtools-messenger-delegated-owner";
const MESSENGER_OWNER_TOKEN_STORAGE_KEY = "teamtools-messenger-owner-token";
const MESSENGER_OWNER_FINGERPRINT_STORAGE_KEY = "teamtools-messenger-owner-fingerprint";

const getMessengerUserStableId = (user) =>
  user?.user_uuid || user?.employee_uuid || user?.id || null;

const generateMessengerOwnerToken = () => {
  try {
    if (window.crypto?.randomUUID) {
      return `tt-owner-${window.crypto.randomUUID()}`;
    }
    if (window.crypto?.getRandomValues) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return `tt-owner-${Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0")
      ).join("")}`;
    }
  } catch (error) {
    // Fall through to Math.random fallback below.
  }

  return `tt-owner-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
};

const getMessengerOwnerFingerprint = async (user) => {
  const stableId = getMessengerUserStableId(user);
  if (!stableId || !window.crypto?.subtle) {
    return null;
  }

  const input = new TextEncoder().encode(`teamtools-messenger-owner:v1:${stableId}`);
  const digest = await window.crypto.subtle.digest("SHA-256", input);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
};

const readMessengerOwnerTokenFromStorage = () => {
  try {
    return localStorage.getItem(MESSENGER_OWNER_TOKEN_STORAGE_KEY) || null;
  } catch (error) {
    return null;
  }
};

const writeMessengerOwnerStateToStorage = (fingerprint, ownerToken) => {
  try {
    if (fingerprint && ownerToken) {
      localStorage.setItem(MESSENGER_OWNER_FINGERPRINT_STORAGE_KEY, fingerprint);
      localStorage.setItem(MESSENGER_OWNER_TOKEN_STORAGE_KEY, ownerToken);
    } else {
      localStorage.removeItem(MESSENGER_OWNER_FINGERPRINT_STORAGE_KEY);
      localStorage.removeItem(MESSENGER_OWNER_TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage errors in private mode or restricted contexts.
  }
};

const readMessengerOwnerFingerprintFromStorage = () => {
  try {
    return localStorage.getItem(MESSENGER_OWNER_FINGERPRINT_STORAGE_KEY) || null;
  } catch (error) {
    return null;
  }
};

const readMessengerDelegatedFromStorage = () => {
  try {
    return sessionStorage.getItem(MESSENGER_DELEGATED_STORAGE_KEY) === "1";
  } catch (error) {
    return false;
  }
};

const writeMessengerDelegatedToStorage = (isDelegated) => {
  try {
    if (isDelegated) {
      sessionStorage.setItem(MESSENGER_DELEGATED_STORAGE_KEY, "1");
    } else {
      sessionStorage.removeItem(MESSENGER_DELEGATED_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage errors in private mode or restricted contexts.
  }
};

const readMessengerDelegatedTabIdFromStorage = () => {
  try {
    const tabId = sessionStorage.getItem(MESSENGER_DELEGATED_TAB_ID_STORAGE_KEY);
    return tabId || null;
  } catch (error) {
    return null;
  }
};

const writeMessengerDelegatedTabIdToStorage = (tabId) => {
  try {
    if (tabId) {
      sessionStorage.setItem(MESSENGER_DELEGATED_TAB_ID_STORAGE_KEY, tabId);
    } else {
      sessionStorage.removeItem(MESSENGER_DELEGATED_TAB_ID_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage errors in private mode or restricted contexts.
  }
};

const readMessengerDelegatedOwnerFromStorage = () => {
  try {
    return sessionStorage.getItem(MESSENGER_DELEGATED_OWNER_STORAGE_KEY) || null;
  } catch (error) {
    return null;
  }
};

const writeMessengerDelegatedOwnerToStorage = (ownerToken) => {
  try {
    if (ownerToken) {
      sessionStorage.setItem(MESSENGER_DELEGATED_OWNER_STORAGE_KEY, ownerToken);
    } else {
      sessionStorage.removeItem(MESSENGER_DELEGATED_OWNER_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage errors in private mode or restricted contexts.
  }
};

// Устанавливаем тон иконкам
setTwoToneColor("#E66A25");

// Основной макет страницы
// Содержит меню, шапку, контент
const MainLayout = ({ config, children, redDot, isAdmin = false }) => {
  const navigate = useNavigate();
  const burgerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const searchInputRef = useRef(null);
  const messengerTabRef = useRef(null);
  const messengerTabBootstrapTimeoutsRef = useRef([]);
  const messengerTabAuthenticatedRef = useRef(false);
  const messengerDelegateProbeRef = useRef(null);
  const messengerHandoffResolverRef = useRef(null);
  const messengerFocusRequestIdRef = useRef(0);
  const messengerTakeoverResolverRef = useRef(null);
  const messengerDelegatedTabIdRef = useRef(readMessengerDelegatedTabIdFromStorage());
  const messengerReopenExternalTabRef = useRef(false);
  const messengerReopenExternalTabTimeoutRef = useRef(null);
  const messengerReopenClosingTabIdRef = useRef(null);
  const messengerExternalTabTransitionPhaseRef = useRef(null);
  const messengerDelegateClearTimeoutRef = useRef(null);
  const { user } = useContext(UserContext);

  const [version, setVersion] = useState();
  const [units, setUnits] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [resources, setResources] = useState([]);
  const [analyticReports, setAnalyticReports] = useState([]);
  const [reports, setReports] = useState([]);
  const [requests, setRequests] = useState([]);
  const [notices, setNotices] = useState([]);
  const [layoutPending, setLayoutPending] = useState(false);
  const [noticesPending, setNoticesPending] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showHeaderSearch, setShowHeaderSearch] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchPending, setSearchPending] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [messengerMode, setMessengerMode] = useState("fixed");
  // eslint-disable-next-line no-unused-vars -- признак непрочитанного больше не красит кнопку
  const [messengerHasUnread, setMessengerHasUnread] = useState(false);
  const [messengerHasIncomingCall, setMessengerHasIncomingCall] = useState(false);
  useUnreadCommands({ setMessengerOpen });
  const [messengerDelegatedToTab, setMessengerDelegatedToTab] = useState(
    readMessengerDelegatedFromStorage
  );
  const [messengerDelegationChecked, setMessengerDelegationChecked] = useState(
    readMessengerDelegatedFromStorage
  );
  const [messengerHandoffRequest, setMessengerHandoffRequest] = useState(0);
  const [messengerEmbedKey, setMessengerEmbedKey] = useState(0);
  const [messengerTakeoverPending, setMessengerTakeoverPending] = useState(false);
  const [messengerOwnerToken, setMessengerOwnerToken] = useState(
    readMessengerOwnerTokenFromStorage
  );

  const showAdminPanel = user?.role === "Administrator";

  useEffect(() => {
    let cancelled = false;

    const syncMessengerOwnerToken = async () => {
      if (!user) {
        writeMessengerOwnerStateToStorage(null, null);
        if (!cancelled) {
          setMessengerOwnerToken(null);
        }
        return;
      }

      const fingerprint = await getMessengerOwnerFingerprint(user);
      if (cancelled) {
        return;
      }

      // If crypto is unavailable, use an ephemeral token for this page lifetime only.
      if (!fingerprint) {
        const ephemeralToken = generateMessengerOwnerToken();
        setMessengerOwnerToken(ephemeralToken);
        return;
      }

      const storedFingerprint = readMessengerOwnerFingerprintFromStorage();
      const storedOwnerToken = readMessengerOwnerTokenFromStorage();
      const ownerToken =
        storedFingerprint === fingerprint && storedOwnerToken
          ? storedOwnerToken
          : generateMessengerOwnerToken();

      writeMessengerOwnerStateToStorage(fingerprint, ownerToken);
      setMessengerOwnerToken(ownerToken);
    };

    void syncMessengerOwnerToken();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }

      // Нужно для рендеринга только одного поповера поиска в шапке или в сайдбаре, а не обоих сразу
      setShowHeaderSearch(window.innerWidth < 960);

      if (window.innerWidth < 960) {
        // Если ширина окна меньше 960 пикселей, боковая панель развернута (используется для мобильной шапки)
        setCollapsed(false);
      } else if (window.innerWidth < 1280) {
        // Если ширина окна меньше 1280 пикселей, сворачиваем боковую панель
        setCollapsed(true);
      } else {
        // Если ширина окна больше 1280 пикселей, боковая панель развернута
        setCollapsed(false);
      }
    };

    fetchData();
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!menuIsOpen) return;
    const handleClickOutside = (event) => {
      // Клик по бургеру — не закрываем здесь, обработает onClick
      if (burgerRef.current && burgerRef.current.contains(event.target)) return;
      // Клик внутри меню — не закрываем
      if (menuRef.current && menuRef.current.contains(event.target)) {
        // если это ссылка — отложим закрытие, чтобы Link успел сработать
        if (event.target.tagName === "A") {
          setTimeout(() => setMenuIsOpen(false), 50);
          return;
        }

        return;
      }
      setMenuIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuIsOpen]);

  useEffect(() => {
    if (!searchOpen) return;

    const query = searchValue.trim();

    if (!query || query?.length < 1) {
      //ANYA три символа
      setSearchPending(false);
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setSearchPending(true);

    const debounceTimer = setTimeout(async () => {
      try {
        const { data, total } = await globalSearch({ search: query }, isAdmin);

        if (requestId !== searchRequestIdRef.current) return;

        setSearchResults(data || []);
        setSearchTotal(total || 0);
      } catch (error) {
        if (requestId !== searchRequestIdRef.current) return;

        setSearchResults([]);
        setSearchTotal(0);
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchPending(false);
        }
      }
    }, 350);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchOpen, searchValue, isAdmin]);

  useEffect(() => {
    if (!config?.messenger_enabled) {
      return undefined;
    }

    const stopAutoRefresh = startMessengerBootstrapAutoRefresh();
    return () => {
      stopAutoRefresh?.();
    };
  }, [config?.messenger_enabled]);

  const fetchData = async () => {
    try {
      const [version, items, { units }, requests, reports] = await Promise.all([
        getVersion(),
        getMenu(),
        getEmployeeUnitsOptions(),
        getRequests(),
        getReports(),
      ]);

      setVersion(version);
      setResources(items);
      setUnits(units);
      setRequests(requests);
      setReports(reports);
    } catch (error) {
      console.log("error", error);
    }
    // Вынесла, т.к. мешал и баговал Fixme: вернуть обратно
    try {
      const analyticReports = await getAnalyticReports();
      setAnalyticReports(analyticReports);
    } catch (error) {
      console.log("error", error);
    }
  };

  const [logoutPending, setLogoutPending] = useState(false);

  const handleLogout = async () => {
    setLogoutPending(true);
    clearCachedMessengerBootstrap();
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.log("Error", err);
    } finally {
      setLogoutPending(false);
    }
  };

  const refreshResources = async () => {
    try {
      const items = await getMenu();
      setResources(items);
    } catch (error) {
      console.log("error", error);
    }
  };

  const refreshAnalyticReports = async () => {
    try {
      const analyticReports = await getAnalyticReports();
      setAnalyticReports(analyticReports);
    } catch (error) {
      console.log("error", error);
    }
  };

  const refreshRequests = async () => {
    try {
      const requests = await getRequests();
      setRequests(requests);
    } catch (error) {
      console.log("error", error);
    }
  };

  const refreshReports = async () => {
    try {
      const reports = await getReports();
      setReports(reports);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleNoticesOpenChange = async (open) => {
    if (!open) return;

    fetchNotices();
  };

  const fetchNotices = async () => {
    setNoticesPending(true);

    try {
      const { notifications } = await getNotices({
        page: 1,
        perpage: 10,
        sort: "-created_at",
        unread_only: "unread",
      });
      setNotices(notifications);
    } catch (error) {
      console.log("error", error);
    } finally {
      setNoticesPending(false);
    }
  };

  // Инвалидируем возможный отложенный ответ и очищаем состояние поиска.
  const resetSearchState = () => {
    searchRequestIdRef.current += 1;
    setSearchPending(false);
    setSearchValue("");
    setSearchResults([]);
    setSearchTotal(0);
  };

  // Обработчик открытия/закрытия поповера поиска. При открытии фокусирует поле ввода, при закрытии сбрасывает состояние поиска.
  const handleSearchOpenChange = (open) => {
    setSearchOpen(open);

    if (open) {
      // Даём время поповеру появиться в DOM перед фокусировкой
      setTimeout(() => searchInputRef.current?.focus(), 50);
      return;
    }

    resetSearchState();
  };

  // Открывает страницу с результатами поиска, передавая текущий запрос в параметрах. Если запрос пустой, ничего не делает.
  const openSearchPage = (rawValue = searchValue) => {
    const query = rawValue.trim();

    if (!query || query?.length < 1) return; //ANYA три символа

    const params = new URLSearchParams({ search: query }).toString();

    navigate(
      isAdmin
        ? `${routes.administration.search}?${params}`
        : `${routes.search}?${params}`,
    );
    setSearchOpen(false);
    resetSearchState();
  };

  const toggleMessengerMode = () => {
    setMessengerMode((currentMode) =>
      currentMode === "floating" ? "fixed" : "floating"
    );
  };

  const resolveMessengerClientOrigin = useCallback(() => {
    if (config?.messenger_client_origin) {
      return config.messenger_client_origin;
    }

    try {
      return new URL(config?.messenger_iframe_url || "").origin;
    } catch (error) {
      return "*";
    }
  }, [config?.messenger_client_origin, config?.messenger_iframe_url]);

  const messengerNotificationSoundUrl = useMemo(
    () => resolveMessengerNotificationSoundUrl(config?.messenger_iframe_url),
    [config?.messenger_iframe_url]
  );

  const messengerRingSoundUrl = useMemo(
    () => resolveMessengerRingSoundUrl(config?.messenger_iframe_url),
    [config?.messenger_iframe_url]
  );

  useEffect(() => {
    if (
      !config?.messenger_enabled ||
      (!messengerNotificationSoundUrl && !messengerRingSoundUrl)
    ) {
      return undefined;
    }

    return initMessengerNotificationSoundUnlock(
      messengerNotificationSoundUrl,
      messengerRingSoundUrl
    );
  }, [
    config?.messenger_enabled,
    messengerNotificationSoundUrl,
    messengerRingSoundUrl,
  ]);

  const onMessengerIncomingNotification = useCallback(
    (payload) => {
      if (payload?.stopSound || payload?.incomingCall === false) {
        setMessengerHasIncomingCall(false);
      } else if (payload?.incomingCall === true || (payload?.playSound && payload?.loop)) {
        setMessengerHasIncomingCall(true);
      }

      dispatchMessengerIncomingNotification({
        soundUrl: messengerNotificationSoundUrl,
        ringSoundUrl: messengerRingSoundUrl,
        payload,
        enableBackgroundNotifications: isMobile || isDesktopNotifyEnabled(),
        onNotificationClick: () => setMessengerOpen(true),
      });
    },
    [isMobile, messengerNotificationSoundUrl, messengerRingSoundUrl]
  );

  const requestMessengerEmbedHandoff = useCallback(() => {
    return new Promise((resolve) => {
      messengerHandoffResolverRef.current = resolve;
      setMessengerHandoffRequest((current) => current + 1);
    });
  }, []);

  const handleMessengerHandoffComplete = useCallback((completed) => {
    messengerHandoffResolverRef.current?.(completed);
    messengerHandoffResolverRef.current = null;
  }, []);

  const clearMessengerReopenExternalTabGuard = useCallback(() => {
    messengerReopenExternalTabRef.current = false;
    messengerReopenClosingTabIdRef.current = null;
    messengerExternalTabTransitionPhaseRef.current = null;
    if (messengerReopenExternalTabTimeoutRef.current) {
      window.clearTimeout(messengerReopenExternalTabTimeoutRef.current);
      messengerReopenExternalTabTimeoutRef.current = null;
    }
  }, []);

  const shouldClearMessengerExternalTabTransitionGuard = useCallback(
    (delegated, tabId = null) => {
      if (!messengerReopenExternalTabRef.current || !delegated) {
        return false;
      }

      if (messengerExternalTabTransitionPhaseRef.current === "closing") {
        return false;
      }

      const closingTabId = messengerReopenClosingTabIdRef.current;
      if (closingTabId && tabId === closingTabId) {
        return false;
      }

      return true;
    },
    []
  );

  const armMessengerReopenExternalTabGuard = useCallback(
    ({ closingTabId = null, phase = "closing" } = {}) => {
      clearMessengerReopenExternalTabGuard();
      messengerReopenExternalTabRef.current = true;
      messengerReopenClosingTabIdRef.current = closingTabId;
      messengerExternalTabTransitionPhaseRef.current = phase;
      messengerReopenExternalTabTimeoutRef.current = window.setTimeout(() => {
        clearMessengerReopenExternalTabGuard();
      }, MESSENGER_REOPEN_EXTERNAL_TAB_TIMEOUT_MS);
    },
    [clearMessengerReopenExternalTabGuard]
  );

  const cancelMessengerDelegatedClear = useCallback(() => {
    if (messengerDelegateClearTimeoutRef.current) {
      window.clearTimeout(messengerDelegateClearTimeoutRef.current);
      messengerDelegateClearTimeoutRef.current = null;
    }
  }, []);

  const applyMessengerDelegatedStateImmediate = useCallback(
    (isDelegated, tabId = null, ownerToken = null) => {
      if (!isDelegated && messengerReopenExternalTabRef.current) {
        return;
      }

      cancelMessengerDelegatedClear();

      setMessengerDelegatedToTab((currentDelegated) => {
        if (currentDelegated && !isDelegated) {
          setMessengerEmbedKey((current) => current + 1);
        }
        return isDelegated;
      });
      messengerDelegatedTabIdRef.current = isDelegated ? tabId : null;
      writeMessengerDelegatedTabIdToStorage(isDelegated ? tabId : null);
      writeMessengerDelegatedOwnerToStorage(isDelegated ? ownerToken : null);
      writeMessengerDelegatedToStorage(isDelegated);
      setMessengerDelegationChecked(true);
    },
    [cancelMessengerDelegatedClear]
  );

  const applyMessengerDelegatedState = useCallback(
    (isDelegated, tabId = null, options = {}) => {
      const { force = false, ownerToken = null } = options;

      if (isDelegated) {
        applyMessengerDelegatedStateImmediate(true, tabId, ownerToken);
        return;
      }

      if (!force && messengerReopenExternalTabRef.current) {
        return;
      }

      if (!force) {
        const trackedTab = messengerTabRef.current;
        if (trackedTab && !trackedTab.closed) {
          return;
        }

        if (messengerDelegateClearTimeoutRef.current) {
          return;
        }

        messengerDelegateClearTimeoutRef.current = window.setTimeout(() => {
          messengerDelegateClearTimeoutRef.current = null;

          if (messengerTabRef.current && !messengerTabRef.current.closed) {
            return;
          }

          applyMessengerDelegatedStateImmediate(false, null, null);
        }, MESSENGER_DELEGATE_CLEAR_DELAY_MS);
        return;
      }

      applyMessengerDelegatedStateImmediate(false, tabId, null);
    },
    [applyMessengerDelegatedStateImmediate]
  );

  useEffect(() => {
    if (!messengerOwnerToken) {
      return;
    }

    const delegatedOwnerToken = readMessengerDelegatedOwnerFromStorage();
    if (readMessengerDelegatedFromStorage() && delegatedOwnerToken !== messengerOwnerToken) {
      applyMessengerDelegatedState(false, null, { force: true });
    }
  }, [applyMessengerDelegatedState, messengerOwnerToken]);

  const resolveMessengerApiBaseUrl = useCallback(() => {
    try {
      const currentUrl = new URL(window.location.origin);

      if (currentUrl.hostname === "localhost" && currentUrl.port === "3000") {
        return "http://localhost:6482";
      }
    } catch (error) {
      // Keep the production fallback below.
    }

    return window.location.origin;
  }, []);

  const buildMessengerTabUrl = useCallback((options = {}) => {
    const iframeUrl = config?.messenger_iframe_url || "";
    if (!iframeUrl) {
      return "";
    }

    try {
      const nextUrl = new URL(iframeUrl);
      nextUrl.searchParams.set("ttv", "tab");
      nextUrl.searchParams.set("apiBaseUrl", resolveMessengerApiBaseUrl());
      if (messengerOwnerToken) {
        nextUrl.searchParams.set("tto", messengerOwnerToken);
      }
      if (options.reopen) {
        nextUrl.searchParams.set("ttb", "reopen");
        nextUrl.searchParams.set("_rt", String(Date.now()));
      }
      return nextUrl.toString();
    } catch (error) {
      const separator = iframeUrl.includes("?") ? "&" : "?";
      const reopenParam = options.reopen
        ? `&ttb=reopen&_rt=${Date.now()}`
        : "";
      const ownerParam = messengerOwnerToken
        ? `&tto=${encodeURIComponent(messengerOwnerToken)}`
        : "";
      return `${iframeUrl}${separator}ttv=tab&apiBaseUrl=${encodeURIComponent(
        resolveMessengerApiBaseUrl()
      )}${ownerParam}${reopenParam}`;
    }
  }, [config?.messenger_iframe_url, messengerOwnerToken, resolveMessengerApiBaseUrl]);

  const buildDelegateProbeUrl = useCallback(() => {
    const iframeUrl = config?.messenger_iframe_url || "";
    if (!iframeUrl) {
      return "";
    }

    try {
      const nextUrl = new URL(iframeUrl);
      nextUrl.searchParams.set("ttv", "delegate-probe");
      return nextUrl.toString();
    } catch (error) {
      const separator = iframeUrl.includes("?") ? "&" : "?";
      return `${iframeUrl}${separator}ttv=${encodeURIComponent("delegate-probe")}`;
    }
  }, [config?.messenger_iframe_url]);

  const getMessengerDelegateProbeWindow = useCallback(() => {
    const probeIframe = messengerDelegateProbeRef?.current;
    if (!probeIframe || !(probeIframe instanceof HTMLIFrameElement)) {
      return null;
    }

    try {
      return probeIframe.contentWindow || null;
    } catch (error) {
      return null;
    }
  }, []);

  const cancelBootstrapDeliveryToMessengerTab = useCallback(() => {
    messengerTabBootstrapTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    messengerTabBootstrapTimeoutsRef.current = [];
  }, []);

  const postBootstrapToMessengerTab = useCallback(async () => {
    const popupWindow = messengerTabRef.current;
    if (!popupWindow || popupWindow.closed) {
      return;
    }

    const bootstrap = await createMessengerBootstrap();
    const activePopupWindow = messengerTabRef.current;
    if (!activePopupWindow || activePopupWindow.closed) {
      return;
    }

    clearCachedMessengerBootstrap();
    activePopupWindow.postMessage(
      {
        type: MESSENGER_BOOTSTRAP_TYPE,
        payload: {
          launchToken: bootstrap.launch_token,
          apiBaseUrl: resolveMessengerApiBaseUrl(),
          title: config?.messenger_title || "Мессенджер",
          ownerToken: messengerOwnerToken,
        },
      },
      config?.messenger_client_origin || "*"
    );
  }, [
    config?.messenger_client_origin,
    config?.messenger_title,
    messengerOwnerToken,
    resolveMessengerApiBaseUrl,
  ]);

  const scheduleBootstrapDeliveryToMessengerTab = useCallback(() => {
    if (messengerTabAuthenticatedRef.current) {
      return;
    }

    cancelBootstrapDeliveryToMessengerTab();
    void postBootstrapToMessengerTab();
    messengerTabBootstrapTimeoutsRef.current = [500, 1500, 3000, 5000].map(
      (delayMs) =>
        window.setTimeout(() => {
          if (messengerTabAuthenticatedRef.current) {
            return;
          }
          void postBootstrapToMessengerTab();
        }, delayMs)
    );
  }, [
    cancelBootstrapDeliveryToMessengerTab,
    postBootstrapToMessengerTab,
  ]);

  const requestMessengerExternalTabFocus = useCallback(() => {
    const requestId = `${Date.now()}-${++messengerFocusRequestIdRef.current}`;
    const targetOrigin = resolveMessengerClientOrigin();

    return new Promise((resolve) => {
      let completed = false;
      let retryTimeoutId;
      let timeoutId;

      const finish = (result) => {
        if (completed) {
          return;
        }

        completed = true;
        window.removeEventListener("message", handleFocusAck);
        window.clearTimeout(retryTimeoutId);
        window.clearTimeout(timeoutId);
        resolve(result);
      };

      const postFocusRequest = () => {
        const probeWindow = getMessengerDelegateProbeWindow();
        if (!probeWindow) {
          finish(false);
          return;
        }

        try {
          probeWindow.postMessage(
            {
              type: MESSENGER_FOCUS_EXTERNAL_TAB_TYPE,
              payload: {
                requestId,
                tabId: messengerDelegatedTabIdRef.current || undefined,
              },
            },
            "*"
          );
        } catch (error) {
          finish(false);
        }
      };

      function handleFocusAck(event) {
        if (targetOrigin !== "*" && event.origin !== targetOrigin) {
          return;
        }

        if (
          event.data?.type === MESSENGER_FOCUS_ACK_TYPE &&
          event.data?.payload?.requestId === requestId
        ) {
          finish(true);
        }
      }

      window.addEventListener("message", handleFocusAck);
      postFocusRequest();

      retryTimeoutId = window.setTimeout(postFocusRequest, 250);
      timeoutId = window.setTimeout(
        () => finish(false),
        MESSENGER_FOCUS_ACK_TIMEOUT_MS
      );
    });
  }, [getMessengerDelegateProbeWindow, resolveMessengerClientOrigin]);

  const requestMessengerTakeover = useCallback(
    (mode = "new-tab", targetTabId = null) => {
      const requestId = `${Date.now()}-takeover-${++messengerFocusRequestIdRef.current}`;
      const tabId = targetTabId || messengerDelegatedTabIdRef.current || undefined;

      return new Promise((resolve) => {
        messengerTakeoverResolverRef.current = {
          requestId,
          resolve,
        };

        const probeWindow = getMessengerDelegateProbeWindow();
        if (!probeWindow) {
          messengerTakeoverResolverRef.current = null;
          resolve(false);
          return;
        }

        try {
          probeWindow.postMessage(
            {
              type: MESSENGER_TAKEOVER_REQUEST_TYPE,
              payload: { requestId, tabId, mode },
            },
            "*"
          );
        } catch (error) {
          messengerTakeoverResolverRef.current = null;
          resolve(false);
          return;
        }

        window.setTimeout(() => {
          if (messengerTakeoverResolverRef.current?.requestId !== requestId) {
            return;
          }
          messengerTakeoverResolverRef.current.resolve(false);
          messengerTakeoverResolverRef.current = null;
        }, MESSENGER_TAKEOVER_TIMEOUT_MS);
      });
    },
    [getMessengerDelegateProbeWindow]
  );

  const waitForMessengerExternalTabToClose = useCallback(
    (trackedTab, timeoutMs = MESSENGER_TAB_CLOSE_WAIT_MS) =>
      new Promise((resolve) => {
        if (!trackedTab || trackedTab.closed) {
          resolve(true);
          return;
        }

        let settled = false;
        const finish = (closed) => {
          if (settled) {
            return;
          }
          settled = true;
          window.removeEventListener("message", onTabClosed);
          window.clearInterval(pollId);
          window.clearTimeout(timeoutId);
          resolve(closed);
        };

        const onTabClosed = (event) => {
          const targetOrigin = resolveMessengerClientOrigin();
          if (targetOrigin !== "*" && event.origin !== targetOrigin) {
            return;
          }

          if (event.data?.type === MESSENGER_TAB_CLOSED_TYPE) {
            finish(true);
          }
        };

        const pollId = window.setInterval(() => {
          if (trackedTab.closed) {
            finish(true);
          }
        }, 100);

        const timeoutId = window.setTimeout(
          () => finish(trackedTab.closed),
          timeoutMs
        );

        window.addEventListener("message", onTabClosed);
      }),
    [resolveMessengerClientOrigin]
  );

  const openMessengerInNewTab = useCallback(async () => {
    const tabUrl = buildMessengerTabUrl();
    if (!tabUrl) {
      return;
    }

    const existingTab =
      messengerTabRef.current && !messengerTabRef.current.closed
        ? messengerTabRef.current
        : null;

    if (existingTab) {
      existingTab.focus();
      setMessengerDelegatedToTab(true);
      writeMessengerDelegatedToStorage(true);
      writeMessengerDelegatedOwnerToStorage(messengerOwnerToken);
      return;
    }

    if (messengerDelegatedToTab) {
      void requestMessengerExternalTabFocus();

      const popupWindow = window.open(tabUrl, MESSENGER_TAB_WINDOW_NAME);
      if (popupWindow) {
        popupWindow.focus();
      }

      setMessengerDelegatedToTab(true);
      writeMessengerDelegatedToStorage(true);
      writeMessengerDelegatedOwnerToStorage(messengerOwnerToken);
      return;
    }

    if (messengerOpen) {
      armMessengerReopenExternalTabGuard({ phase: "opening" });
      const handoffCompleted = await Promise.race([
        requestMessengerEmbedHandoff(),
        new Promise((resolve) => {
          window.setTimeout(() => resolve(false), MESSENGER_HANDOFF_TIMEOUT_MS);
        }),
      ]);
      if (!handoffCompleted) {
        // Still open the tab; Element will retry lock acquisition after stale timeout.
        console.warn("TeamTools messenger handoff timed out before opening tab");
      }
      setMessengerDelegatedToTab(true);
      writeMessengerDelegatedToStorage(true);
      writeMessengerDelegatedOwnerToStorage(messengerOwnerToken);
    }

    const popupWindow = window.open(tabUrl, MESSENGER_TAB_WINDOW_NAME);
    if (!popupWindow) {
      if (messengerOpen) {
        clearMessengerReopenExternalTabGuard();
        setMessengerDelegatedToTab(false);
        writeMessengerDelegatedToStorage(false);
        writeMessengerDelegatedOwnerToStorage(null);
        setMessengerEmbedKey((current) => current + 1);
      }
      return;
    }

    messengerTabRef.current = popupWindow;
    messengerTabAuthenticatedRef.current = false;
    writeMessengerDelegatedToStorage(true);
    writeMessengerDelegatedOwnerToStorage(messengerOwnerToken);
    popupWindow.focus();
    scheduleBootstrapDeliveryToMessengerTab();
  }, [
    armMessengerReopenExternalTabGuard,
    buildMessengerTabUrl,
    clearMessengerReopenExternalTabGuard,
    messengerDelegatedToTab,
    messengerOwnerToken,
    messengerOpen,
    scheduleBootstrapDeliveryToMessengerTab,
    requestMessengerEmbedHandoff,
    requestMessengerExternalTabFocus,
  ]);

  const reopenMessengerExternalTab = useCallback(async () => {
    const tabUrl = buildMessengerTabUrl({ reopen: true });
    if (!tabUrl) {
      return;
    }

    const closingTab = messengerTabRef.current;

    setMessengerTakeoverPending(true);
    armMessengerReopenExternalTabGuard({
      closingTabId: messengerDelegatedTabIdRef.current,
      phase: "closing",
    });
    try {
      await requestMessengerTakeover("new-tab");
      const tabClosed = await waitForMessengerExternalTabToClose(closingTab);

      if (closingTab && !tabClosed && !closingTab.closed) {
        console.warn("TeamTools messenger reopen aborted: external tab did not close");
        clearMessengerReopenExternalTabGuard();
        return;
      }

      if (messengerTabRef.current?.closed) {
        messengerTabRef.current = null;
      }

      messengerExternalTabTransitionPhaseRef.current = "opening";

      const popupWindow = window.open(tabUrl, MESSENGER_TAB_WINDOW_NAME);
      if (popupWindow) {
        messengerTabRef.current = popupWindow;
        messengerTabAuthenticatedRef.current = false;
        popupWindow.focus();
        scheduleBootstrapDeliveryToMessengerTab();
      }
      setMessengerDelegatedToTab(true);
      writeMessengerDelegatedToStorage(true);
      writeMessengerDelegatedOwnerToStorage(messengerOwnerToken);
    } finally {
      setMessengerTakeoverPending(false);
    }
  }, [
    armMessengerReopenExternalTabGuard,
    buildMessengerTabUrl,
    clearMessengerReopenExternalTabGuard,
    messengerOwnerToken,
    scheduleBootstrapDeliveryToMessengerTab,
    requestMessengerTakeover,
    waitForMessengerExternalTabToClose,
  ]);

  const continueMessengerHere = useCallback(async () => {
    clearMessengerReopenExternalTabGuard();
    setMessengerTakeoverPending(true);
    try {
      await requestMessengerTakeover("embed");
    } finally {
      setMessengerTakeoverPending(false);
    }

    applyMessengerDelegatedState(false, null, { force: true });
    setMessengerOpen(true);
  }, [
    applyMessengerDelegatedState,
    clearMessengerReopenExternalTabGuard,
    requestMessengerTakeover,
  ]);

  useEffect(() => {
    return () => {
      clearMessengerReopenExternalTabGuard();
      cancelMessengerDelegatedClear();
    };
  }, [cancelMessengerDelegatedClear, clearMessengerReopenExternalTabGuard]);

  useEffect(() => {
    const handleMessengerTabMessage = (event) => {
      const targetOrigin = resolveMessengerClientOrigin();
      if (
        targetOrigin !== "*" &&
        !messengerOriginsMatch(targetOrigin, event.origin)
      ) {
        return;
      }

      if (event.data?.type === MESSENGER_DELEGATED_STATE_TYPE) {
        const delegated = Boolean(event.data?.payload?.delegated);
        const tabId = event.data?.payload?.tabId || null;
        const ownerToken = event.data?.payload?.ownerToken || null;

        if (delegated && !messengerOwnerToken) {
          return;
        }

        if (delegated && ownerToken !== messengerOwnerToken) {
          void requestMessengerTakeover("embed", tabId).finally(() => {
            messengerTabRef.current = null;
            applyMessengerDelegatedState(false, null, { force: true });
          });
          return;
        }

        if (shouldClearMessengerExternalTabTransitionGuard(delegated, tabId)) {
          clearMessengerReopenExternalTabGuard();
        }

        applyMessengerDelegatedState(delegated, tabId, { ownerToken });
        return;
      }

      if (event.data?.type === MESSENGER_READY_TYPE) {
        if (!messengerTabAuthenticatedRef.current) {
          scheduleBootstrapDeliveryToMessengerTab();
        }
        return;
      }

      if (event.data?.type === MESSENGER_AUTHENTICATED_TYPE) {
        messengerTabAuthenticatedRef.current = true;
        cancelBootstrapDeliveryToMessengerTab();
        return;
      }

      if (event.data?.type === MESSENGER_RECOVER_SESSION_TYPE) {
        void postBootstrapToMessengerTab();
        return;
      }

      if (event.data?.type === MESSENGER_UNREAD_TYPE) {
        setMessengerHasUnread(Boolean(event.data?.payload?.hasUnread));
        return;
      }

      if (
        event.data?.type === MESSENGER_NOTIFICATION_TYPE ||
        event.data?.type === MESSENGER_PLAY_SOUND_TYPE
      ) {
        const payload = event.data?.payload;
        if (payload && typeof payload === "object") {
          onMessengerIncomingNotification(payload);
        } else {
          onMessengerIncomingNotification({ playSound: true });
        }
        return;
      }

      if (event.data?.type === MESSENGER_TAB_CLOSED_TYPE) {
        if (messengerTabRef.current && !messengerTabRef.current.closed) {
          return;
        }
        applyMessengerDelegatedState(false, null, { force: true });
        messengerTabAuthenticatedRef.current = false;
        cancelBootstrapDeliveryToMessengerTab();
        messengerTabRef.current = null;
        return;
      }

      if (
        event.data?.type === MESSENGER_TAKEOVER_ACK_TYPE &&
        event.data?.payload?.requestId
      ) {
        const resolver = messengerTakeoverResolverRef.current;
        if (resolver?.requestId === event.data.payload.requestId) {
          resolver.resolve(Boolean(event.data?.payload?.accepted));
          messengerTakeoverResolverRef.current = null;
        }
      }
    };

    window.addEventListener("message", handleMessengerTabMessage);
    return () => window.removeEventListener("message", handleMessengerTabMessage);
  }, [
    applyMessengerDelegatedState,
    cancelBootstrapDeliveryToMessengerTab,
    clearMessengerReopenExternalTabGuard,
    messengerOwnerToken,
    postBootstrapToMessengerTab,
    requestMessengerTakeover,
    resolveMessengerClientOrigin,
    messengerNotificationSoundUrl,
    onMessengerIncomingNotification,
    scheduleBootstrapDeliveryToMessengerTab,
    shouldClearMessengerExternalTabTransitionGuard,
  ]);

  useEffect(() => {
    return () => {
      cancelBootstrapDeliveryToMessengerTab();
    };
  }, [cancelBootstrapDeliveryToMessengerTab]);

  useEffect(() => {
    if (!config?.messenger_enabled) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (messengerReopenExternalTabRef.current) {
        return;
      }

      const trackedTab = messengerTabRef.current;
      if (!trackedTab) {
        return;
      }

      if (!trackedTab.closed) {
        applyMessengerDelegatedState(true, messengerDelegatedTabIdRef.current, {
          ownerToken: messengerOwnerToken,
        });
        return;
      }

      messengerTabRef.current = null;
      applyMessengerDelegatedState(false, null, { force: true });
    }, MESSENGER_TAB_SYNC_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [applyMessengerDelegatedState, config?.messenger_enabled, messengerOwnerToken]);

  const handleSearchResultSelect = () => {
    setSearchOpen(false);
    resetSearchState();
  };

  // Содержимое поповера поиска
  const searchPopoverContent = (
    <Flex vertical gap={24} className={styles.searchPopoverContent}>
      <InputFrom3
        ref={searchInputRef}
        allowClear
        placeholder="Начните вводить"
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
        onPressEnter={() => openSearchPage()}
      />

      {searchPending ? (
        <Skeleton
          active
          className={styles.searchSkeleton}
          title={false}
          paragraph={{
            rows: 3,
          }}
        />
      ) : (
        !!searchResults?.length && (
          <Flex vertical>
            {searchResults.map((result) => (
              <SearchResultItem
                key={`${result.type}-${result.data.uuid}`}
                result={result}
                isAdmin={isAdmin}
                onSelect={handleSearchResultSelect}
              />
            ))}
          </Flex>
        )
      )}

      <Flex justify="space-between" align="center" gap={10}>
        <div className={styles.searchMeta}>
          {" "}
          {/* ANYA: вернуть три символа */}
          {!searchPending &&
            searchValue.trim().length >= 1 &&
            `Найдено ${searchTotal} ${getPluralizedWord(searchTotal, ["объект", "объекта", "объектов"])} по всем разделам`}
        </div>
        {searchValue.trim().length < 1 ? (
          <Button onClick={() => setSearchOpen(false)}>Отмена</Button>
        ) : (
          <Button onClick={() => openSearchPage()}>Показать все</Button>
        )}
      </Flex>
    </Flex>
  );

  // if (logoutPending) return <LoadingScreen />;

  return (
    <Spin
      tip={layoutPending && "Загрузка..."}
      spinning={logoutPending || layoutPending}
    >
      <Layout className={styles.page}>
        <Header className={styles.header}>
          {/* Лого */}
          <Link to={routes.dashboards}>
            <Flex gap={12} align="center">
              {config?.logo_path && (
                <img
                  src={config?.logo_path}
                  width={32}
                  height={32}
                  alt="Логотип"
                />
              )}
              {version && (
                <div className={styles.version}>
                  v{version?.version}
                  <br></br>
                  {version?.date}
                </div>
              )}
            </Flex>
          </Link>

          <Flex gap={8} className={styles.headerActions}>
            {/* Поиск */}
            {showHeaderSearch && (
              <Popover
                trigger="click"
                arrow={false}
                open={searchOpen}
                onOpenChange={handleSearchOpenChange}
                placement="bottom"
                align={{
                  offset: [0, 8],
                }}
                content={searchPopoverContent}
              >
                <div
                  className={`${styles.mobileSearchBtn} ${searchOpen && styles.isActive}`}
                >
                  <SearchIcon
                    className={styles.searchIcon}
                    style={{ fontSize: 20 }}
                  />
                </div>
              </Popover>
            )}

            {/* Бургер меню */}
            <div
              ref={burgerRef}
              className={`${styles.burger} ${menuIsOpen && styles.isOpen}`}
              onClick={() => setMenuIsOpen(!menuIsOpen)}
            >
              {menuIsOpen ? <CloseIcon /> : <MenuIcon />}
            </div>
          </Flex>
        </Header>

        <Sider
          width="260px"
          trigger={null}
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          className={`${styles.aside} ${menuIsOpen && styles.isOpen}`}
        >
          <div className={styles.asideInner} ref={menuRef}>
            <Logo
              collapsed={collapsed}
              version={version}
              logo={config?.logo_path}
              logo_text={config?.system_name}
              isRed={config?.is_red_logo}
              className={styles.asideHeader}
            />

            {!showHeaderSearch && (
              <div className={styles.asideSearchBlock}>
                <Popover
                  trigger="click"
                  arrow={false}
                  open={searchOpen}
                  onOpenChange={handleSearchOpenChange}
                  placement="rightTop"
                  align={{
                    offset: [10, -20],
                  }}
                  content={searchPopoverContent}
                >
                  <Flex
                    align="center"
                    gap={10}
                    className={`${styles.asideSearchBtn} ${searchOpen ? styles.isActive : ""}`}
                  >
                    <SearchIcon
                      className={styles.searchIcon}
                      style={{ fontSize: 20 }}
                    />
                    {!collapsed && "Поиск"}
                  </Flex>
                </Popover>
              </div>
            )}

            <div className={styles.asideMenu} id="aside-nav">
              {isAdmin && (
                <div
                  style={{
                    color: "var(--color-deep-blue)",
                    paddingLeft: 6,
                    lineHeight: 2,
                    whiteSpace: "nowrap",
                    marginBottom: 10,
                  }}
                >
                  Адм{collapsed ? "." : "инистративная панель"}
                </div>
              )}

              <Menu
                isAdmin={isAdmin}
                resources={resources}
                units={units}
                analyticReports={analyticReports}
                requests={requests}
                reports={reports}
                config={config}
              />
            </div>
            <UnreadPanel collapsed={collapsed} config={config} />
            <div
              className={[
                styles.asideFooter,
                collapsed && styles.collapsed,
                config?.messenger_enabled && styles.withMessengerWidget,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <Flex className={styles.asideFooterIcons} id="aside-footer">
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "profile",
                        label: (
                          <Link
                            to={routes.profile}
                            onClick={() => setMenuIsOpen(false)}
                          >
                            <Flex gap={8} align="center">
                              <UserIcon
                                style={{ fontSize: 16, color: "#BBBBBB" }}
                              />
                              Открыть профиль
                            </Flex>
                          </Link>
                        ),
                      },
                      // {
                      //   key: "settings",
                      //   label: (
                      //     <Link to={"#"} onClick={() => setMenuIsOpen(false)}>
                      //       <Flex gap={8} align="center">
                      //         <SettingsIcon
                      //           style={{ fontSize: 16, color: "#BBBBBB" }}
                      //         />
                      //         Настройки пользователя
                      //       </Flex>
                      //     </Link>
                      //   ),
                      // },
                      {
                        key: "settings",
                        label: (
                          <Link
                            to={routes.notices("settings")}
                            onClick={() => setMenuIsOpen(false)}
                          >
                            <Flex gap={8} align="center">
                              <SettingsIcon
                                style={{ fontSize: 16, color: "#BBBBBB" }}
                              />
                              Настройки пользователя
                            </Flex>
                          </Link>
                        ),
                      },
                      showAdminPanel &&
                        (isAdmin
                          ? {
                              key: "admin",
                              label: (
                                <Link
                                  to={routes.tasks}
                                  onClick={() => setMenuIsOpen(false)}
                                >
                                  <Flex gap={8} align="center">
                                    <LoginIcon
                                      style={{ fontSize: 16, color: "#BBBBBB" }}
                                    />
                                    Выйти из админ панели
                                  </Flex>
                                </Link>
                              ),
                            }
                          : {
                              key: "admin",
                              label: (
                                <Link
                                  to={routes.administration.tasks}
                                  onClick={() => setMenuIsOpen(false)}
                                >
                                  <Flex gap={8} align="center">
                                    <AdminIcon
                                      style={{ fontSize: 16, color: "#BBBBBB" }}
                                    />
                                    Администрирование
                                  </Flex>
                                </Link>
                              ),
                            }),
                      {
                        key: "logout",
                        label: (
                          <Link to={"#"} onClick={handleLogout}>
                            <Flex gap={8} align="center">
                              <LogoutIcon
                                style={{ fontSize: 16, color: "#BBBBBB" }}
                              />
                              Выйти из Системы
                            </Flex>
                          </Link>
                        ),
                      },
                    ],
                  }}
                  placement="top"
                >
                  <Button
                    color="default"
                    variant="link"
                    icon={<UserCircleIcon style={{ fontSize: 20 }} />}
                    style={{ padding: 0 }}
                    className={styles.asideFooterIcon}
                  />
                </Dropdown>
                <Popover
                  content={
                    <NewsFeed
                      notices={notices}
                      pending={noticesPending}
                      reloadData={() => fetchNotices()}
                    />
                  }
                  placement="top"
                  arrow={false}
                  onOpenChange={handleNoticesOpenChange}
                >
                  <Button
                    color="default"
                    variant="link"
                    icon={
                      <NoticesCountBadge fallbackDot={redDot}>
                        <BellIcon style={{ fontSize: 20 }} />
                      </NoticesCountBadge>
                    }
                    style={{ padding: 0 }}
                    className={styles.asideFooterIcon}
                  />
                </Popover>
              </Flex>
              <Button
                id="collapsed-btn"
                className={styles.toggleButton}
                type="text"
                icon={collapsed ? <DoubleRightIcon /> : <DoubleLeftIcon />}
                onClick={() => setCollapsed(!collapsed)}
              />
            </div>
          </div>
        </Sider>

        <Layout
          className={`${styles.body} main-body`}
          style={{
            marginInlineStart: !collapsed ? 260 : 60,
          }}
        >
          <Content
            style={{
              padding: "10px 20px ",
              overflow: "auto",
            }}
          >
            <MainContext.Provider
              value={{
                resourcesMenu: resources,
                refreshResources,
                refreshAnalyticReports,
                refreshRequests,
                refreshReports,
                setLayoutPending,
                isMobile,
                config,
              }}
            >
              {children}
            </MainContext.Provider>
          </Content>
        </Layout>
        {config?.messenger_enabled
          ? withOptionalTooltip(
              !isMobile,
              {
                placement: "left",
                title: messengerOpen ? "Закрыть мессенджер" : "Открыть мессенджер",
              },
              <Button
                type="primary"
                shape="circle"
                size="large"
                className={`${styles.messengerFab} ${
                  messengerHasIncomingCall
                    ? styles.messengerFabUnread
                    : ""
                }`}
                icon={
                  messengerOpen ? (
                    <CloseIcon className={styles.messengerFabIcon} />
                  ) : (
                    <ChatAltIcon className={styles.messengerFabIcon} />
                  )
                }
                aria-label={
                  messengerOpen ? "Закрыть мессенджер" : "Открыть мессенджер"
                }
                onClick={() => setMessengerOpen((prevState) => !prevState)}
              />
            )
          : null}
        {config?.messenger_enabled ? <MessengerCountBadge config={config} /> : null}
      </Layout>
      {config?.messenger_enabled ? (
        <MessengerFloatingWindow
          open={messengerOpen}
          mode={messengerMode}
          isMobile={isMobile}
          onClose={() => setMessengerOpen(false)}
          config={config}
          onUnreadStateChange={setMessengerHasUnread}
          onToggleMode={toggleMessengerMode}
          onOpenInNewTab={openMessengerInNewTab}
          delegatedToTab={messengerDelegatedToTab}
          delegationChecked={messengerDelegationChecked}
          handoffRequest={messengerHandoffRequest}
          onHandoffComplete={handleMessengerHandoffComplete}
          embedKey={messengerEmbedKey}
          onReopenExternalTab={reopenMessengerExternalTab}
          onContinueHere={continueMessengerHere}
          takeoverPending={messengerTakeoverPending}
          ownerToken={messengerOwnerToken}
        />
      ) : null}
      <BrowserNotifyPrompt config={config} />
      {config?.messenger_enabled && buildDelegateProbeUrl() ? (
        <iframe
          ref={messengerDelegateProbeRef}
          src={buildDelegateProbeUrl()}
          title="messenger-delegate-probe"
          aria-hidden="true"
          tabIndex={-1}
          style={{
            width: 0,
            height: 0,
            border: 0,
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
          }}
        />
      ) : null}
    </Spin>
  );
};

export default MainLayout;
